import { Edit } from "@mui/icons-material"
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid2 as Grid,
    MenuItem,
    Stack,
    Switch,
} from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3"
import { isAfter, isEqual, isValid } from "date-fns"
import { useEffect, useState } from "react"
import { useEditSensorInstall } from "../../../api/SensorInstallApi"
import { NexDropzone } from "../../../components/Dropzone"
import { LabelAndText } from "../../../components/Typography/Typography"
import { CreateSensorInstallInfo, SensorInstallInfo } from "../../../models/sensors/SensorInstallInfo"
import { TimeUtilsDateFns } from "../../../utils/TimeUtils"

interface EditSensorInstallMenuItemProps {
    sensorInstall: SensorInstallInfo
    closeMenu: () => void
}

export const EditSensorInstallMenuItem = (props: EditSensorInstallMenuItemProps) => {
    const { sensorInstall, closeMenu } = props
    const [dialogOpen, setDialogOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    const sensorInstallCurrentFile = new File([sensorInstall.file_data], sensorInstall.file_name, {
        type: sensorInstall.file_format,
    })
    const sensorInstallCurrentStartDate = TimeUtilsDateFns.getDateFromFormattedDay(sensorInstall.valid_start)
    const sensorInstallCurrentEndDate =
        sensorInstall.valid_end != null ? TimeUtilsDateFns.getDateFromFormattedDay(sensorInstall.valid_end) : undefined

    const [boresightFile, setBoresightFile] = useState<File>(sensorInstallCurrentFile)
    const [inputValidStartDate, setValidStartDate] = useState<Date | undefined>(sensorInstallCurrentStartDate)
    const [inputValidEndDate, setValidEndDate] = useState<Date | undefined>(sensorInstallCurrentEndDate)
    const [canEditEndDate, setCanEditEndDate] = useState(sensorInstall.valid_end != null)

    const useSensorInstallMutation = useEditSensorInstall()

    const endDateIsInvalid = !!(
        inputValidEndDate &&
        inputValidStartDate &&
        !isAfter(inputValidEndDate, inputValidStartDate) &&
        !isEqual(inputValidEndDate, inputValidStartDate)
    )

    useEffect(() => {
        return handleReset
    }, [])

    const handleFileSelect = (files: File[]) => {
        // only one file is allowed on upload
        setBoresightFile(files[0])
    }

    const handleReset = () => {
        setBoresightFile(sensorInstallCurrentFile)
        setValidStartDate(sensorInstallCurrentStartDate)
        setValidEndDate(sensorInstallCurrentEndDate)
        setErrorMessage("")
        useSensorInstallMutation.reset()
    }

    const handleClose = () => {
        handleReset()
        setDialogOpen(false)
    }

    return (
        <>
            <MenuItem onClick={() => setDialogOpen(true)}>
                <Edit /> Edit
            </MenuItem>
            <Dialog open={dialogOpen} maxWidth={"sm"} fullWidth={true}>
                <DialogTitle>Edit Sensor Installation</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Box>
                            <LabelAndText showDivider={false} label="Date Uploaded">
                                {TimeUtilsDateFns.formatDate(new Date(sensorInstall.date_uploaded))}
                            </LabelAndText>
                            <LabelAndText showDivider={false} label="Filename">
                                {sensorInstall.file_name}
                            </LabelAndText>
                            <LabelAndText showDivider={false} label="Sensor Model - Sensor Serial Number">
                                {sensorInstall.sensor.sensor_model} - {sensorInstall.sensor.serial_number}
                            </LabelAndText>
                        </Box>

                        <Divider />

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Grid container spacing={4}>
                                <Grid size={6}>
                                    <DatePicker
                                        disabled={true}
                                        label="OLD Valid Start Date"
                                        value={TimeUtilsDateFns.getDateFromFormattedDay(sensorInstall.valid_start)}
                                        slotProps={{ textField: { size: "small" } }}
                                        format={TimeUtilsDateFns.dayFormat}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <DatePicker
                                        label="NEW Valid Start Date"
                                        value={inputValidStartDate ?? null}
                                        onChange={(value) => setValidStartDate(value ?? undefined)}
                                        slotProps={{
                                            field: { clearable: true },
                                            textField: {
                                                size: "small",
                                                helperText: "Required",
                                                error: !inputValidStartDate || !isValid(inputValidStartDate),
                                            },
                                        }}
                                        format={TimeUtilsDateFns.dayFormat}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={4}>
                                <Grid size={6}>
                                    {sensorInstall.valid_end != null && (
                                        // is currently inactive
                                        <DatePicker
                                            disabled={true}
                                            label="OLD Valid End Date"
                                            value={TimeUtilsDateFns.getDateFromFormattedDay(sensorInstall.valid_end)}
                                            slotProps={{ textField: { size: "small" } }}
                                            format={TimeUtilsDateFns.dayFormat}
                                        />
                                    )}

                                    {sensorInstall.valid_end == null && (
                                        // Is currently active
                                        <FormControlLabel
                                            label={"Mark as inactive?"}
                                            control={
                                                <Switch
                                                    checked={canEditEndDate}
                                                    onChange={(_, checked) => setCanEditEndDate(checked)}
                                                />
                                            }
                                            // so ui doesn't change when the helper text for the controlled datePicker appears
                                            sx={{ pb: 4 }}
                                        />
                                    )}
                                </Grid>
                                <Grid size={6}>
                                    {!canEditEndDate && (
                                        <DatePicker
                                            label="Present"
                                            disabled={true}
                                            slotProps={{ textField: { size: "small" } }}
                                        />
                                    )}

                                    {canEditEndDate && (
                                        <DatePicker
                                            label={"NEW Valid End Date"}
                                            value={inputValidEndDate ?? null}
                                            onChange={(value) => setValidEndDate(value ?? undefined)}
                                            slotProps={{
                                                field: { clearable: true },
                                                textField: {
                                                    size: "small",
                                                    helperText: endDateIsInvalid
                                                        ? "End Date must be after or equal to Start Date"
                                                        : "Required",
                                                    error:
                                                        !inputValidEndDate ||
                                                        !isValid(inputValidEndDate) ||
                                                        endDateIsInvalid,
                                                },
                                            }}
                                            format={TimeUtilsDateFns.dayFormat}
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </LocalizationProvider>

                        <NexDropzone
                            acceptedFileTypes={[".ini", ".boresight"]}
                            filesLimit={1}
                            maxFileSize={5e7}
                            files={boresightFile && [boresightFile]}
                            onChange={handleFileSelect}
                        />

                        {useSensorInstallMutation.isError && (
                            <Alert severity="error">
                                {errorMessage ?? `Unable to edit sensor installation with id ${sensorInstall.id}`}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={
                            sensorInstall.sensor.sensor_id == null ||
                            boresightFile == null ||
                            !isValid(inputValidStartDate) ||
                            (canEditEndDate && !isValid(inputValidEndDate)) ||
                            endDateIsInvalid
                        }
                        variant="contained"
                        onClick={async () => {
                            if (
                                sensorInstall.sensor.sensor_id &&
                                boresightFile &&
                                inputValidStartDate &&
                                isValid(inputValidStartDate)
                            ) {
                                const canHaveValidEnd = !!(
                                    canEditEndDate &&
                                    inputValidEndDate &&
                                    isValid(inputValidEndDate)
                                )
                                const newSensorInstallInfo: CreateSensorInstallInfo = {
                                    id: sensorInstall.id,
                                    sensor_id: sensorInstall.sensor.sensor_id,
                                    valid_start: TimeUtilsDateFns.formatDate(inputValidStartDate),
                                    valid_end: canHaveValidEnd
                                        ? TimeUtilsDateFns.formatDate(inputValidEndDate)
                                        : undefined,
                                    date_uploaded: sensorInstall.date_uploaded,
                                    file_name: boresightFile.name,
                                    file_data: await boresightFile.text(),
                                    file_size: boresightFile.size,
                                    file_format: boresightFile.type,
                                }
                                await useSensorInstallMutation.mutateAsync(newSensorInstallInfo, {
                                    onSuccess: async () => {
                                        setErrorMessage("")
                                        handleClose()
                                        closeMenu()
                                    },
                                    onError: (error) => {
                                        setErrorMessage(error.message)
                                    },
                                })
                            }
                        }}
                    >
                        Update Installation
                    </Button>
                    <Button color="error" onClick={handleClose}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
